import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MembershipTierService } from '../../../core/api-services/membership-tier.service';
import { SubscriptionBillingService } from '../../../core/api-services/subscription-billing.service';
import { formatRenewalLine } from '../../../core/utils/subscription-billing-utils';

/**
 * Manage Subscription (Task Group 9)
 *
 * Route: /billing. Pro-only — a Core org lands on Upgrade to Pro instead
 * (FR-5.3), same pattern as UpgradeToProComponent's inverse redirect.
 *
 * Also the Hub's default return destination for BOTH the subscription
 * Checkout return (via the dedicated /billing/subscription/return route,
 * which redirects here after refreshing tier state) and the Customer
 * Portal return (portal-link's own Hub-side default returnPath is
 * literally `/billing` — no dedicated return route needed there, since
 * this page's own ngOnInit already re-fetches on every load).
 *
 * Reference: agent-os/specs/2026-06-29-core-pro-membership/spec.md FR-5
 */
@Component({
  selector: 'app-manage-subscription',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="container py-5 mw-640px" *ngIf="ready()">
      <h2 class="mb-4">Manage Subscription</h2>

      <div class="card">
        <div class="card-body p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Pro Plan</h5>
            <span class="badge bg-primary">{{ statusLabel() }}</span>
          </div>
          <p class="mb-4" [ngClass]="isCancelling() ? 'text-warning fw-semibold' : 'text-muted'"><i *ngIf="isCancelling()" class="bi bi-exclamation-triangle-fill me-1"></i>{{ renewalLine() }}</p>

          <button
            class="btn btn-outline-primary"
            [disabled]="submitting()"
            (click)="openPortal()"
          >
            <span *ngIf="submitting()" class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Redirecting...</span>
            </span>
            {{ submitting() ? 'Redirecting…' : 'Manage in Stripe' }}
          </button>
          <p *ngIf="errorMessage()" class="text-danger small mt-3 mb-0">{{ errorMessage() }}</p>
          <p class="text-muted small mt-3 mb-0">
            Update your payment method, cancel, or view invoices via Stripe's secure Customer Portal.
          </p>
        </div>
      </div>
    </div>
  `,
})
export class ManageSubscriptionComponent implements OnInit {
  private router = inject(Router);
  private membershipTierService = inject(MembershipTierService);
  private billingService = inject(SubscriptionBillingService);

  ready = signal(false);
  submitting = signal(false);
  errorMessage = signal('');

  statusLabel = signal('');
  renewalLine = signal('');
  isCancelling = signal(false);

  async ngOnInit(): Promise<void> {
    // Always re-fetch on load, not just await the cached signal — this
    // page is also where both the Checkout return AND the Customer
    // Portal return land, so a stale cached value (e.g. read right after
    // a cancellation) would show pre-change state for one extra visit.
    await this.membershipTierService.fetchStatus();

    if (!this.membershipTierService.isPro()) {
      this.router.navigate(['/billing/upgrade']);
      return;
    }

    this.statusLabel.set(this.formatStatus(this.membershipTierService.status()));
    this.renewalLine.set(
      formatRenewalLine(this.membershipTierService.renewalDate(), this.membershipTierService.cancelAtPeriodEnd()),
    );
    this.isCancelling.set(this.membershipTierService.cancelAtPeriodEnd() === true);
    this.ready.set(true);
  }

  async openPortal(): Promise<void> {
    this.submitting.set(true);
    this.errorMessage.set('');
    try {
      const portal = await this.billingService.createPortalLink();
      this.billingService.redirect(portal.url);
    } catch (e: unknown) {
      this.submitting.set(false);
      // A hub ApiError, or the redirect guard's Error; both carry a displayable message.
      this.errorMessage.set(e instanceof Error && e.message ? e.message : 'Could not open the billing portal. Please try again.');
    }
  }

  private formatStatus(status: string | null): string {
    if (!status) return 'Active';
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
